import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { NumericFormat as NumberFormat } from "react-number-format";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import ConditionalButton from "../../../layout/conditional-button";
import ErrorModal from "../../../layout/modal";
import DeleteModal from "../../../layout/modal";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getCode, save as saveCode, destroy } from "../../../actions/expense.codes";

const STYLE_VALUES = ["H", "C", "F"];

class Detail extends React.Component {
    
    constructor(props) {
        super(props);
        this.props.loading(true);
        this.state = {
            data: this.mapPropsToState(props),
            modal: {
                error: {
                    show: false
                },
                delete: {
                    show: false
                }
            }
        };
        this.defaultCountry = "us";
        this.props.pageInfo({ title: "Expense Code", breadcrumb: `[New Code]` });
        this.changes = {};
    }

    mapPropsToState = (props) => {
        const { uuid, code, description, caption, merchant, quantity, each, begins, ends, minimum, maximum } = props;
        return {
            uuid, code, description, caption, merchant, quantity, each, begins, ends, minimum, maximum
        }
    }

    get uuid() {
        return this.props.params.uuid;
    }

    loadData = async () => {
        this.props.loading(true);
        if (isGuid(this.uuid)) {
            const data = await this.props.getCode({ uuid: this.uuid });
            this.setState({
                data
            });
            await this.props.pageInfo({ title: `Edit: ${data.caption} (${data.code})`, breadcrumb: `${data.caption}` });
        }
        this.props.loading(false);
    }

    componentDidMount = () => {
        setTimeout(async () => {
            await this.loadData();
        }, 30);
    }

    doPostChange = () => {
        if (this.props.onChange) this.props.onChange(this.state);
    }

    onChange = (e) => {
        this.setState(prev =>({
            data: {
                ...prev.data,
                [e.target.name]: e.target.value
            }
        }), this.doPostChange);
    }

    onValueChange = (e) => {
        this.onChange({ target: { name: "value", value: e } });
    }

    onCaptionChange = (e) => {
        if (this.state.data.caption === this.state.data.description) {
            this.setState(prev => ({
                data: {
                    ...prev.data,
                    caption: e.target.value,
                    description: e.target.value
                }
            }), this.doPostChange);
        }
    }

    onSaveClick = async (e) => {
        this.props.loading(true);
        const code = await this.props.saveCode({ ...this.state.data, uuid: isGuid(this.uuid) ? this.uuid : uuidv4() });
        window.location.href = `/dashboard/settings/expenses/${code?.uuid}`;
    }

    onDeleteClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    title: "Delete expense",
                    body: `This action will permanently delete the code ${this.state.data?.caption}. This will not affect previously billed entries using this expense.`,
                    show: true
                }
            }
        }));
    }
    

    onDeleteOkModal = (e) => {
        this.props.destroy(this.state.uuid);
        this.onDeleteHideModal(e);
        this.props.navigate("/dashboard/settings/expenses");
    }

    onDeleteHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    show: false
                }
            }
        }));
    }

    onErrorHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                error: {
                    show: false
                }
            }
        }))
    }


    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Save Rate" display={true} enabled={true} onEnabledClick={this.onSaveClick} icon="save">Save</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-danger" tooltip="Delete expense" display={true} enabled={true} onEnabledClick={this.onDeleteClick} icon="bin">Delete</ConditionalButton>
                </Col>
                
                <Col xs="12">
                    <Row className="">
                        <Col xs="12" md="8" xl="9">
                            <div className="mb-3 top-label">
                                <Form.Control type="text" name="caption" value={this.state.data?.caption || ""} onChange={this.onCaptionChange} />
                                <Form.Label>CAPTION</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12" md="4" xl="3">
                            <div className="mb-3 top-label">
                                <Form.Control type="text" name="code" value={this.state.data?.code || ""} onChange={this.onChange} />
                                <Form.Label>CODE</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div className="mb-3 top-label">
                                <Form.Control type="text" name="description" value={this.state.data?.description || ""} onChange={this.onChange} />
                                <Form.Label>DESCRIPTION</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div className="mb-3 top-label">
                                <Form.Control type="text" name="merchant" value={this.state.data?.merchant || ""} onChange={this.onChange} />
                                <Form.Label>MERCHANT</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12" md="6" xl="3">
                            <div className="mb-3 top-label">
                                <NumberFormat
                                    name="quantity"
                                    id="quantity"
                                    value={this.state.data?.quantity ?? ""}              // can be number or string
                                    thousandSeparator
                                    allowNegative={false}
                                    inputMode="decimal"
                                    decimalScale={0}
                                    className="text-end form-control mb-2"
                                    isAllowed={({ floatValue, value }) => {
                                        if (value === "" || floatValue == null) return true;
                                        return floatValue > 0 && floatValue < 999999
                                    }}
                                    onValueChange={({ floatValue, value }) => {
                                        this.onChange({ target: { name: "quantity", value: floatValue } });  
                                    }}
                                />
                                <Form.Label>QTY</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12" md="6" xl="3">
                            <div className="mb-3 top-label">
                                <NumberFormat
                                    name="each"
                                    id="each"
                                    value={this.state.data?.each ?? ""}              // can be number or string
                                    thousandSeparator
                                    allowNegative={false}
                                    inputMode="decimal"
                                    decimalScale={2}
                                    prefix="$"
                                    className="text-end form-control mb-2"
                                    isAllowed={({ floatValue, value }) =>{
                                        if (value === "" || floatValue == null) return true;
                                        return floatValue > 0 && floatValue < 9999999
                                    }}
                                    onValueChange={({ floatValue, value }) => {
                                        this.onChange({ target: { name: "each", value: floatValue } });  
                                    }}
                                />
                                <Form.Label>EACH</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12" md="6" xl="3">
                            <div className="mb-3 top-label">
                                <NumberFormat
                                    name="minimum"
                                    id="minimum"
                                    value={this.state.data?.minimum ?? ""}              // can be number or string
                                    thousandSeparator
                                    allowNegative={false}
                                    inputMode="decimal"
                                    decimalScale={0}
                                    className="text-end form-control mb-2"
                                    isAllowed={({ floatValue, value }) => {
                                        if (value === "" || floatValue == null) return true;
                                        return floatValue > 0 && floatValue < 99999 && floatValue < (this.state.maximum ?? 99999)
                                    }}
                                    onValueChange={({ floatValue, value }) => {
                                        this.onChange({ target: { name: "minimum", value: floatValue } });  
                                    }}
                                />
                                <Form.Label>MIN</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12" md="6" xl="3">
                            <div className="mb-3 top-label">
                                <NumberFormat
                                    name="maximum"
                                    id="maximum"
                                    value={this.state.data?.maximum ?? ""}              // can be number or string
                                    thousandSeparator
                                    allowNegative={false}
                                    inputMode="decimal"
                                    decimalScale={0}
                                    className="text-end form-control mb-2"
                                    isAllowed={({ floatValue, value }) => {
                                        if (value === "" || floatValue == null) return true;
                                        return floatValue > 0 && floatValue < 99999 && floatValue > (this.state.minimum ?? 0)
                                    }}
                                    onValueChange={({ floatValue, value }) => {
                                        this.onChange({ target: { name: "maximum", value: floatValue } });  
                                    }}
                                />
                                <Form.Label>MAX</Form.Label>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="mb-3 top-label">
                                <DatePicker id="begins" name="begins" className="form-control" selected={this.state.data?.begins} onChange={(date) => this.onChange({ target: { name: "begins", value: date } })} />
                                <Form.Label>BEGINING ON</Form.Label>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="mb-3 top-label">
                                <DatePicker isClearable id="ends" name="ends" className="form-control" selected={this.state.data?.ends} onChange={(date) => this.onChange({ target: { name: "ends", value: date } })} />
                                <Form.Label>ENDING ON</Form.Label>
                            </div>
                        </Col>
                    </Row>
                </Col>
                <ErrorModal show={this.state.modal.error.show} title={this.state.modal.error.title} body={this.state.modal.error.body} buttons={
                    [
                        {
                            default: true,
                            caption: "ok",
                            onEnabledClick: this.onErrorHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]
                }></ErrorModal>
                <DeleteModal show={this.state.modal.delete.show} title={this.state.modal.delete.title} body={this.state.modal.delete.body} buttons={
                    [
                        {
                            caption: "ok",
                            onEnabledClick: this.onDeleteOkModal,
                            variant: "primary",
                            display: true,
                            enabled: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onDeleteHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]
                }></DeleteModal>
            </>
        );
    }
}

Detail.propTypes = {
    auth: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default withNavigation(connect(mapStateToProps, { pageInfo, loading, getCode, saveCode, destroy })(Detail));
